import 'date-fns'
import React from 'react'
import { DatePicker } from '@mui/x-date-pickers'
import { useIntl } from 'react-intl'
import { withStyles } from '@mui/styles'
import { Typography } from '@mui/material'

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'

import { getISOStringInitDate } from '../../core/utils/dateUtils'
import { dateInputStyles } from './date-input.styles'
import { es } from 'date-fns/locale/es'

/**
 * @typedef {object} DateInputChangeEvent
 * @property {object} target - Un objeto que simula la estructura de un evento de cambio nativo.
 * @property {string} target.name - El nombre del campo, proveniente de la prop name.
 * @property {string} target.value - La fecha seleccionada en formato ISO string.
 */

/**
 * @typedef {object} DateInputProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por withStyles.
 * @property {boolean} [readOnly=false] - Si es true, el campo se muestra como texto de solo lectura.
 * @property {boolean} [required=false] - Indica si el campo es obligatorio.
 * @property {Date | string} [minDate] - Fecha mínima seleccionable.
 * @property {Date | string} [maxDate] - Fecha máxima seleccionable.
 * @property {boolean} [allowKeyboard=true] - Si es true, permite la entrada de fecha mediante teclado.
 * @property {string} [value] - El valor actual del campo en formato ISO string.
 * @property {string} [title] - El título o etiqueta que se muestra sobre el campo.
 * @property {string} [name] - El nombre del campo, que se incluirá en el objeto de evento del onChange.
 * @property {'small' | 'normal'} [fontSize] - Controla el tamaño de la fuente del título y valor readOnly.
 * @property {function(DateInputChangeEvent): void} onChange - La función callback que se ejecuta cuando cambia la fecha.
 */

/**
 * Un componente selector de fechas que utiliza Material-UI DatePicker.
 *
 * Proporciona una interfaz de calendario para seleccionar fechas, con soporte para
 * restricciones de fechas mínimas y máximas, modo de solo lectura, y formato de fecha
 * localizado. Los valores se manejan internamente como objetos Date de JavaScript y se
 * emiten como strings ISO en el evento onChange.
 *
 * @component
 * @example
 * const MyForm = () => {
 *   const [fechaNacimiento, setFechaNacimiento] = useState('');
 *
 *   const handleChange = (e) => {
 *     console.log(`El campo '${e.target.name}' ahora es: ${e.target.value}`);
 *     setFechaNacimiento(e.target.value);
 *   };
 *
 *   return (
 *     <DateInput
 *       title="Fecha de Nacimiento"
 *       name="fechaNacimiento"
 *       value={fechaNacimiento}
 *       onChange={handleChange}
 *       required
 *       maxDate={new Date()}
 *       minDate={new Date('1900-01-01')}
 *     />
 *   );
 * };
 *
 * @param {DateInputProps} props - Las props del componente DateInput.
 */
export const DateInput = withStyles(dateInputStyles)(({
  classes,
  readOnly = false,
  minDate,
  maxDate,
  allowKeyboard = true,
  ...props
}) => {
  const { formatMessage, formatDate } = useIntl()
  const typographyVariant = props.fontSize === 'small' ? '2' : '1'

  const handleChange = (date, value) => {
    if (date && !isNaN(date.getTime())) {
      props.onChange({ target: { name: props.name, value: getISOStringInitDate(date) } })
    }
  }

  const refuseDatePattern =
    '^(?:(?:31(\\/|-|\\.)(?:0?[13578]|1[02]))\\1|(?:(?:29|30)(\\/|-|\\.)(?:0?[13-9]|1[0-2])\\2))(?:(?:1[6-9]|[2-9]\\d)?\\d{2})$|^(?:29(\\/|-|\\.)0?2\\3(?:(?:(?:1[6-9]|[2-9]\\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\\d|2[0-8])(\\/|-|\\.)(?:(?:0?[1-9])|(?:1[0-2]))\\4(?:(?:1[6-9]|[2-9]\\d)?\\d{2})$'

  return (
    <div className={classes.root}>
      {props.title ? (
        <Typography variant={`subtitle${typographyVariant}`} className={classes.title}>
          {props.title}
          {props.required ? <span style={{ color: '#B3272C' }}> *</span> : null}
        </Typography>
      ) : null}
      {readOnly ? (
        <Typography
          variant={`body${typographyVariant}`}
          color="textSecondary"
          style={{ whiteSpace: 'pre-line' }}
        >
          {props.value ? formatDate(props.value) : '--'}
        </Typography>
      ) : (
        <>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <DatePicker
              {...props}
              id={props.name}
              name={props.name}
              value={props.value ? new Date(props.value) : null}
              onChange={handleChange}
              minDate={minDate}
              maxDate={maxDate}
              format="dd/MM/yyyy"
              inputVariant="outlined"
              InputProps={{ className: classes.input, style: { backgroundColor: 'white' } }}
              okLabel={formatMessage({ id: 'picker.ok' })}
              cancelLabel={formatMessage({ id: 'picker.cancel' })}
            />
          </LocalizationProvider>
        </>
      )}
    </div>
  )
})
